import { createSelector } from "@reduxjs/toolkit";
import type { ColDef, ColGroupDef } from "ag-grid-enterprise";
import type { IRoomSummary } from "@backend/src/shared/schemas/schemas";
import { DR_STATUS_COLORS, MMI_COLORS } from "../../RoomColoring";
import { selectValidationColumnMetas } from "./roomTableDrofusOccurrenceStandardValidationSelectors";
import { selectSpecifiedArticlesValidationColumnMetas } from "./roomTableDrofusOccurrenceArticleValidationSelectors";
import { buildValidationColumns } from "./buildValidationColumns";

const MMI_COLOR_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(MMI_COLORS).map(([k, v]) => [String(k), v])
);

export const ROOMS_TABLE_DEFAULT_COL_DEF: ColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  flex: undefined,
  enableRowGroup: true,
  enablePivot: true,
  enableValue: true,
};

const buildBaseColumnDefs = (): ColDef<IRoomSummary>[] => [
  {
    colId: "roomNumber",
    headerName: "Rom",
    field: "roomNumber",
    sortable: true,
    filter: true,
    minWidth: 160,
    cellRenderer: "agGroupCellRenderer",
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: "RoomNumberLinkRenderer",
    },
  },
  {
    colId: "roomName",
    headerName: "Navn",
    field: "roomName",
    sortable: true,
    filter: true,
    flex: 1,
  },
  {
    colId: "drStatus",
    headerName: "Dr status",
    field: "drStatus",
    sortable: true,
    filter: true,
    width: 140,
    cellRenderer: "ColoredBadgeCellRenderer",
    cellRendererParams: {
      colorMap: DR_STATUS_COLORS,
      titlePrefix: "dR-status",
      toKey: (v: unknown) => (typeof v === "string" ? v.trim() : ""),
    },
  },
  {
    colId: "rfpStatus",
    headerName: "dRofus-status",
    field: "rfpStatus",
    sortable: true,
    filter: true,
    flex: 1,
  },
  {
    colId: "rfpMalRom",
    headerName: "Standardrom",
    field: "rfpMalRom",
    sortable: true,
    filter: true,
    width: 140,
  },
  {
    colId: "supplyAirFlow",
    headerName: "Tilluft (m³/h)",
    field: "supplyAirFlow",
    sortable: true,
    filter: "agNumberColumnFilter",
    width: 140,
  },
  {
    colId: "returnAirFlow",
    headerName: "Avtrekk (m³/h)",
    field: "returnAirFlow",
    sortable: true,
    filter: "agNumberColumnFilter",
    width: 150,
  },
  {
    colId: "extraIfcInfo.pns",
    headerName: "PNS",
    valueGetter: (p) => p.data?.extraIfcInfo?.pns ?? null,
    width: 140,
    sortable: true,
    filter: true,
  },
  {
    colId: "extraIfcInfo.mmi",
    headerName: "MMI",
    valueGetter: (p) => p.data?.extraIfcInfo?.mmi ?? null,
    width: 140,
    sortable: true,
    filter: true,
    cellRenderer: "ColoredBadgeCellRenderer",
    cellRendererParams: {
      colorMap: MMI_COLOR_MAP,
      titlePrefix: "MMI",
      toKey: (v: unknown) => {
        if (typeof v === "number" && Number.isFinite(v)) return String(v);
        if (typeof v === "string") return v.trim();
        return "";
      },
    },
  },
  {
    colId: "extraIfcInfo.floor",
    headerName: "Etasje",
    valueGetter: (p) => p.data?.extraIfcInfo?.floor ?? null,
    width: 120,
    sortable: true,
    filter: true,
  },
];

export const selectRoomsTableStandardValidationColDefs = createSelector(
  [selectValidationColumnMetas],
  (metas): ColDef<IRoomSummary>[] =>
    buildValidationColumns(metas, {
      percentCellRenderer: "PercentCellRenderer",
      statusCellRenderer: "ValidationStatusBadgeCellRenderer",
    })
);

export const selectRoomsTableSpecifiedArticlesValidationColDefs = createSelector(
  [selectSpecifiedArticlesValidationColumnMetas],
  (metas): ColDef<IRoomSummary>[] =>
    buildValidationColumns(metas, {
      percentCellRenderer: "PercentCellRenderer",
      statusCellRenderer: "ValidationStatusBadgeCellRenderer",
    })
);

export const selectRoomsTableColumnDefsWithGroups = createSelector(
  [selectRoomsTableStandardValidationColDefs, selectRoomsTableSpecifiedArticlesValidationColDefs],
  (
    standardValidationColDefs,
    specifiedArticlesValidationColDefs
  ): Array<ColDef<IRoomSummary> | ColGroupDef<IRoomSummary>> => {
    const baseColumnDefs = buildBaseColumnDefs();

    const roomCols = baseColumnDefs.filter((c) => ["roomNumber", "roomName"].includes(String(c.colId)));
    const drofusCols = baseColumnDefs.filter((c) =>
      ["rfpStatus", "rfpMalRom", "drStatus"].includes(String(c.colId))
    );
    const luftCols = baseColumnDefs.filter((c) =>
      ["supplyAirFlow", "returnAirFlow"].includes(String(c.colId))
    );
    const revitCols = baseColumnDefs.filter((c) =>
      ["ceilingCoordinate", "ceilingDirection"].includes(String(c.colId))
    );
    const ifcCols = baseColumnDefs.filter((c) =>
      ["extraIfcInfo.pns", "extraIfcInfo.mmi", "extraIfcInfo.floor"].includes(String(c.colId))
    );

    return [
      { headerName: "Romdata", children: roomCols },
      { headerName: "dRofus", children: drofusCols },
      { headerName: "Luftmengder", children: luftCols },
      { headerName: "Revit", children: revitCols },
      { headerName: "IFC", children: ifcCols },
      { headerName: "Standardromvalideringer", children: standardValidationColDefs },
      { headerName: "Forekomstvalideringer", children: specifiedArticlesValidationColDefs },
    ];
  }
);

export const selectRoomsTableAllColIds = createSelector(
  [selectRoomsTableStandardValidationColDefs, selectRoomsTableSpecifiedArticlesValidationColDefs],
  (standardValidationColDefs, specifiedArticlesValidationColDefs): string[] => {
    const base = buildBaseColumnDefs()
      .map((c) => c.colId)
      .filter((id): id is string => typeof id === "string");
    const dynStd = standardValidationColDefs
      .map((c) => c.colId)
      .filter((id): id is string => typeof id === "string");
    const dynArt = specifiedArticlesValidationColDefs
      .map((c) => c.colId)
      .filter((id): id is string => typeof id === "string");

    return [...base, ...dynStd, ...dynArt];
  }
);

export const selectRoomsTableColumnsSignature = createSelector(
  [selectRoomsTableAllColIds],
  (allColIds): string => allColIds.join("|")
);
